import { TimecodePill } from '@src/components/common/TimecodePill';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { getMuxHostedThumbnailUrl } from '@src/utility/story';
import { timeFormat } from '@src/utility/timeFormat';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

type Props = {
  /** Time under the cursor, seconds. */
  time: number;
  /** Cursor x in viewport coordinates. */
  clientX: number;
  /** Top edge of the scrubber rail in viewport coordinates. */
  railTop: number;
  framerate: number;
  /**
   * Mux asset to pull the frame from — the clip's OWN rendered asset, whose
   * timeline matches the scrubber's. Omit for a clip that has never been
   * published; the card then shows only the timecode.
   */
  playbackId?: string;
  /**
   * Which scene the cursor is over, e.g. "Scene 7 of 12". Omitted for a clip
   * with no scenes, or wherever the scenes strip is not shown — the card is
   * then exactly the frame + timecode it has always been.
   */
  sceneLabel?: string;
};

/**
 * Bounding box for the frame. The clip's own asset can be any aspect ratio, so
 * both dimensions are caps rather than fixed sizes: 16:9 lands 160×90, 1:1
 * lands 160×160, and 9:16 — which at width-only sizing would tower to 284px —
 * lands 113×200 instead. Mux fits within the same box, so the bytes match what
 * is displayed.
 */
const CARD_MAX_WIDTH = 160;
const CARD_MAX_HEIGHT = 200;
/** Gap between the card's bottom edge and the rail. */
const RAIL_GAP = 12;
/** Keeps the card off the viewport edges when hovering near 0:00 or the end. */
const VIEWPORT_MARGIN = 8;

/**
 * Frame + timecode preview that follows the cursor along the clip-preview
 * scrubber (PLAT-10954).
 *
 * Portaled and `position: fixed` because the control bar's `Wrapper` is both a
 * stacking context and a `container-type: inline-size` containment context — an
 * absolutely-positioned card would be clipped by it. Portaling also means this
 * works unchanged in pseudo-fullscreen, where the whole bar is itself portaled
 * to `document.body`. It also keeps the card out of the bar's height, which
 * `ControlBarSpacer` in `SocialClipShareModal` hardcodes.
 *
 * The frame comes from the clip's own published render. In Edit Mode the canvas
 * is a cross-origin Creatomate iframe that cannot be captured, and mapping back
 * into the source video would be wrong wherever the clip has transcript cuts —
 * so a previously-rendered frame is the only honest source, at the cost of being
 * stale if the clip changed since it was last published.
 */
export const ScrubberHoverPreview = ({
  time,
  clientX,
  railTop,
  framerate,
  playbackId,
  sceneLabel,
}: Props) => {
  // Whole seconds: the readout is MM:SS, so the quantized frame and the
  // displayed time always agree, and one URL per second means the browser cache
  // does the throttling — no debounce, and no request per mousemove.
  const thumbnailUrl = playbackId
    ? getMuxHostedThumbnailUrl(playbackId, Math.floor(time), {
        // 2x for retina; `preserve` fits inside the box without cropping, so a
        // tall clip comes back narrow rather than centre-cropped.
        width: CARD_MAX_WIDTH * 2,
        height: CARD_MAX_HEIGHT * 2,
        fitMode: 'preserve',
      })
    : undefined;

  // Mux 404s briefly on a freshly published asset. Rather than retry, fall back
  // to the timecode alone — a hover preview is transient, and an empty image box
  // is worse than no image box.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  useEffect(() => {
    // A new asset deserves a fresh attempt; a new *frame* of a failed asset
    // does not, so key the reset on the id rather than the url.
    setFailedUrl(null);
  }, [playbackId]);

  const showThumbnail = !!thumbnailUrl && failedUrl !== thumbnailUrl;

  // Centre on the cursor, then clamp so the card never leaves the viewport.
  // The pointer stays under the middle of the card until the clamp bites, which
  // is how every other video scrubber behaves.
  // Clamped against the widest the card can be. A narrower (taller) clip sits
  // slightly further from the edge than it strictly needs to — invisible, and
  // cheaper than measuring the image to find out.
  const half = CARD_MAX_WIDTH / 2;
  const left = Math.min(
    Math.max(clientX, VIEWPORT_MARGIN + half),
    window.innerWidth - VIEWPORT_MARGIN - half,
  );

  return ReactDOM.createPortal(
    <Card
      data-testid="scrubber-hover-preview"
      style={{ left, top: railTop - RAIL_GAP }}
    >
      {showThumbnail && (
        <Thumbnail
          src={thumbnailUrl}
          alt=""
          data-testid="scrubber-hover-thumbnail"
          onError={() => setFailedUrl(thumbnailUrl)}
        />
      )}
      <Readout $wide={!!sceneLabel}>
        <Timecode>{timeFormat(time, framerate, true)}</Timecode>
        {sceneLabel && (
          <SceneName data-testid="scrubber-hover-scene">{sceneLabel}</SceneName>
        )}
      </Readout>
    </Card>,
    document.body,
  );
};

export default ScrubberHoverPreview;

/**
 * Anchored above the rail and centred on the cursor. Sits just below the
 * comment markers' hover card so that, if both ever resolve at once, the
 * comment (which the user deliberately pointed at) wins.
 */
const Card = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: ${CLIP_PREVIEW_Z.quickEditPopover - 1};
  transform: translate(-50%, -100%);
  pointer-events: none;
`;

/**
 * Timecode and scene on one line, the way every mainstream player reads out a
 * scrubber position — the frame above, then "where am I" beneath it. Built on
 * the shared `TimecodePill` so this and the comment-marker drag label stay one
 * object visually; only the layout is added here.
 *
 * Capped at the frame's own maximum width so a long scene name can never make
 * the card wider than the thumbnail it labels (which would also push it past
 * the viewport clamp the caller computes from `CARD_MAX_WIDTH`). Only widened
 * to that cap when there is a scene to name — a bare timecode stays the small
 * chip it is today.
 */
const Readout = styled(TimecodePill)<{ $wide: boolean }>`
  display: inline-flex;
  align-items: baseline;
  /* Wide enough that the timecode and the scene read as two fields rather than
     one run-on string — the gap is the separator, so no glyph is needed. */
  gap: 8px;
  max-width: ${({ $wide }) => ($wide ? `${CARD_MAX_WIDTH}px` : 'none')};
  /* The pill is the only thing that can overflow the flex column above it. */
  box-sizing: border-box;
`;

/** Fixed-width digits, so the readout doesn't jitter under a moving cursor. */
const Timecode = styled.span`
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

/**
 * Secondary to the timecode: the position is what the creator is aiming at,
 * the scene is context. Truncated rather than wrapped — the card tracks the
 * pointer, so a second line would make it change height as it moves.
 */
const SceneName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-muted);
`;

/**
 * The frame, bounded on both axes so a portrait clip can't tower over the bar.
 * Auto width/height against two maxima preserves the clip's own aspect ratio —
 * no cropping and no letterboxing, at the cost of the card settling to its
 * final size once the first frame loads.
 */
const Thumbnail = styled.img`
  display: block;
  max-width: ${CARD_MAX_WIDTH}px;
  max-height: ${CARD_MAX_HEIGHT}px;
  width: auto;
  height: auto;
  border-radius: var(--radius, 8px);
  border: 1px solid var(--border-subtle);
  box-shadow: var(--elevation-md);
  background: var(--surface);
`;
